import { compact } from "lodash";

import { toast } from "@eisox/design-system";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Navigate } from "@tanstack/react-router";

import { buildingApi, meterApi } from "~/api";
import {
  chartQueries,
  ConsumptionTab,
  ConsumptionToolbar,
  drillDown,
  finerGranularities,
  hierarchyQueries,
  MeterConsumption,
  resolveChartView,
  SubMeterBreakdown,
  subMetersOf,
  type ConsumptionSearch,
} from "~/features/consumption";
import { todayIn } from "~/helpers/day-range.helper";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/consumption/$meterId")({
  loaderDeps: ({ search }) => search,
  loader: async ({ context: { queryClient }, params: { buildingId, meterId }, deps: search }) => {
    const [meters, building] = await Promise.all([
      queryClient.ensureQueryData(meterApi.queries.building(buildingId)),
      queryClient.ensureQueryData(buildingApi.queries.byId(buildingId)),
    ]);
    const meter = meters.find(({ id }) => id === meterId);
    if (!meter) return;
    const view = resolveChartView(search, todayIn(building.timezone), building, meter);
    if (search.tab === ConsumptionTab.HIERARCHY) {
      const subMeterIds = subMetersOf(meters, meterId).map(({ id }) => id);
      const queries = hierarchyQueries(building, meterId, subMeterIds, view.period);
      await Promise.all([queries.meter, ...queries.subMeters].map(query => queryClient.ensureQueryData(query)));
      return;
    }
    const queries = chartQueries(building, meterId, view);
    await Promise.all(compact([queries.current, queries.compared]).map(query => queryClient.ensureQueryData(query)));
  },
  component: MeterRoute,
});

function MeterRoute() {
  const { buildingId, meterId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { building } = useBuildingContext(Route.id);
  const { data: meters } = useSuspenseQuery(meterApi.queries.building(buildingId));
  const meter = meters.find(({ id }) => id === meterId);

  if (!meter) {
    return <Navigate to="/buildings/$buildingId/consumption" params={{ buildingId }} search replace />;
  }

  const subMeters = subMetersOf(meters, meter.id);
  const parent = meters.find(({ id }) => id === meter.parentId);
  if (search.tab === ConsumptionTab.HIERARCHY && subMeters.length === 0) {
    return parent ? (
      <Navigate
        to="/buildings/$buildingId/consumption/$meterId"
        params={{ buildingId, meterId: parent.id }}
        search
        replace
      />
    ) : (
      <Navigate from={Route.fullPath} search={{ ...search, tab: ConsumptionTab.CHART }} replace />
    );
  }

  const today = todayIn(building.timezone);
  const view = resolveChartView(search, today, building, meter);

  const handleSearchChange = (patch: Partial<ConsumptionSearch>) => {
    void navigate({ search: previous => ({ ...previous, ...patch }) });
  };

  const openBucket = (targetMeterId: string, start: string, compared?: boolean) => {
    const drilled = drillDown(view, start, building, today, compared);
    if (drilled?.reason) toast.info(drilled.reason);
    if (targetMeterId === meterId && !drilled?.search) return;
    void navigate({
      to: "/buildings/$buildingId/consumption/$meterId",
      params: { buildingId, meterId: targetMeterId },
      search: previous => ({ ...previous, ...drilled?.search, tab: ConsumptionTab.CHART }),
    });
  };

  const handleMeterChange = (nextMeterId: string) => {
    void navigate({
      to: "/buildings/$buildingId/consumption/$meterId",
      params: { buildingId, meterId: nextMeterId },
      search: true,
    });
  };

  return (
    <>
      <ConsumptionToolbar
        meters={meters}
        meter={meter}
        tab={search.tab}
        view={view}
        today={today}
        hasHierarchy={subMeters.length > 0 || parent !== undefined}
        onMeterChange={handleMeterChange}
        onSearchChange={handleSearchChange}
      />
      {search.tab === ConsumptionTab.HIERARCHY ? (
        <SubMeterBreakdown meter={meter} subMeters={subMeters} period={view.period} onBucketOpen={openBucket} />
      ) : (
        <MeterConsumption
          meter={meter}
          view={view}
          today={today}
          onSearchChange={handleSearchChange}
          onBucketOpen={
            finerGranularities(view.period.granularity).length > 0
              ? (start, compared) => openBucket(meterId, start, compared)
              : undefined
          }
        />
      )}
    </>
  );
}
